import React, { useState } from 'react';
import { 
    PlusIcon, 
    TrashIcon,
    CalendarIcon,
    CheckCircleIcon,
    GlobeAltIcon,
    ExclamationCircleIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import Helper from "../helpers";
import config from '../config';

const baseUrl = config.apiBaseUrl;

const BulkClass = () => {
    const [classes, setClasses] = useState([]);
    const [className, setClassName] = useState('');
    const [description, setDescription] = useState('');
    const [startDate, setStartDate] = useState(dayjs());
    const [price, setPrice] = useState(0);
    const [cycleType, setCycleType] = useState(1); // Default to Weekly
    const [isPublic, setIsPublic] = useState(false);
    const [venueKeyOptions, setVenueKeyOptions] = useState([]);
    const [currentVenueKey, setCurrentVenueKey] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState(null);
    const [messageType, setMessageType] = useState(''); // 'success' or 'error'

    const cycleTypes = [
        { value: 0, label: 'Daily' },
        { value: 1, label: 'Weekly' },
        { value: 2, label: 'Monthly' },
        { value: 3, label: 'Quarterly' },
        { value: 4, label: 'Yearly' }
    ];

    // Helper function to show messages
    const showMessage = (text, type) => {
        setMessage(text);
        setMessageType(type);
        setTimeout(() => {
            setMessage(null);
            setMessageType('');
        }, 5000);
    };

    // Load admin venues
    React.useEffect(() => {
        Helper.getAdminVenues(setCurrentVenueKey, setVenueKeyOptions);
    }, []);

    const handleAddClass = () => {
        if (!className.trim()) {
            showMessage('Please enter a class name', 'error');
            return;
        }
        if (!currentVenueKey) {
            showMessage('Please select a venue', 'error');
            return;
        }
        
        setClasses([...classes, {
            name: className,
            description,
            // No trailing Z: this is a local wall-clock time, not UTC. Appending Z here
            // previously made the backend/browser round-trip treat it as UTC, shifting the
            // displayed time by the local timezone offset (e.g. an hour out during BST).
            startDate: startDate.format('YYYY-MM-DDTHH:mm:ss'),
            price,
            cycleType,
            isPublic
        }]);
        setClassName('');
        setDescription('');
        setStartDate(dayjs());
        setPrice(0);
        setCycleType(1); // Reset to Weekly
        setIsPublic(false);
        
        showMessage(`Added "${className}" to the list`, 'success');
    };

    const handleRemoveClass = (index) => {
        setClasses(classes.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        if (classes.length === 0) {
            showMessage('No classes to create. Please add some classes first.', 'error');
            return;
        }

        if (!currentVenueKey) {
            showMessage('Please select a venue before creating classes.', 'error');
            return;
        }

        setIsSubmitting(true);
        const token = await Helper.getCookie('wh-jwt');
        
        let successCount = 0;
        let errorCount = 0;
        const errors = [];

        try {
            for (let i = 0; i < classes.length; i++) {
                const c = classes[i];
                try {
                    const response = await fetch(`${baseUrl}/api/classes/admin`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            venueKey: currentVenueKey,
                            name: c.name,
                            description: c.description,
                            startDate: c.startDate,
                            price: c.price,
                            cycleType: c.cycleType,
                            isPublic: c.isPublic
                        }),
                    });

                    if (response.ok) {
                        successCount++;
                    } else {
                        const errorText = await response.text();
                        errorCount++;
                        errors.push(`${c.name}: ${errorText || 'Unknown error'}`);
                    }
                } catch (error) {
                    errorCount++;
                    errors.push(`${c.name}: ${error.message || 'Network error'}`);
                }
            }

            // Show results
            if (successCount === classes.length) {
                showMessage(`Successfully created all ${successCount} classes!`, 'success');
                setClasses([]);
            } else if (successCount > 0) {
                showMessage(
                    `Created ${successCount} classes successfully. ${errorCount} failed: ${errors.join(', ')}`, 
                    'error'
                );
                // Remove successfully created classes from the list
                // This would require tracking which ones succeeded, for now we'll keep them all
            } else {
                showMessage(`Failed to create any classes. Errors: ${errors.join(', ')}`, 'error');
            }

        } catch (error) {
            showMessage(`Unexpected error occurred: ${error.message}`, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="card p-6 mb-6">
                <h1 className="text-3xl font-bold text-white mb-4">Bulk Add Classes</h1>
                
                {/* Message Display */}
                {message && (
                    <div className={`mb-4 p-4 rounded-lg flex items-center ${
                        messageType === 'success' 
                            ? 'bg-green-900/20 border border-green-600 text-green-400' 
                            : 'bg-red-900/20 border border-red-600 text-red-400'
                    }`}>
                        {messageType === 'success' ? (
                            <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                        ) : (
                            <ExclamationCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                        )}
                        <span className="flex-1">{message}</span>
                        <button
                            onClick={() => setMessage(null)}
                            className="ml-2 text-gray-400 hover:text-white"
                        >
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    </div>
                )}
                
                {/* Venue Selector */}
                {venueKeyOptions.length > 0 && (
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Select Venue
                        </label>
                        <select
                            value={currentVenueKey}
                            onChange={(e) => setCurrentVenueKey(e.target.value)}
                            className="input-field max-w-xs"
                        >
                            <option value="">Select a venue...</option>
                            {venueKeyOptions.map((venueKey) => (
                                <option key={venueKey} value={venueKey}>
                                    {venueKey}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* Add Class Form */}
            <div className="card p-6 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">Add New Class</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Class Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Class Name
                        </label>
                        <input
                            type="text"
                            value={className}
                            onChange={e => setClassName(e.target.value)}
                            className="input-field"
                            placeholder="Enter class name"
                        />
                    </div>

                    {/* Price */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Price (£)
                        </label>
                        <input
                            type="number"
                            value={price}
                            onChange={e => setPrice(Number(e.target.value))}
                            className="input-field"
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                        />
                    </div>

                    {/* Cycle Type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Recurrence
                        </label>
                        <select
                            value={cycleType}
                            onChange={e => setCycleType(Number(e.target.value))}
                            className="input-field"
                        >
                            {cycleTypes.map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Start Date */}
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Start Date & Time
                        </label>
                        <input
                            type="datetime-local"
                            value={startDate.format('YYYY-MM-DDTHH:mm')}
                            onChange={e => setStartDate(dayjs(e.target.value))}
                            className="input-field"
                        />
                    </div>

                    {/* Description */}
                    <div className="md:col-span-2 lg:col-span-3">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Description
                        </label>
                        <textarea
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            className="input-field"
                            placeholder="Class description..."
                            rows="3"
                        />
                    </div>

                    {/* Public Class Checkbox */}
                    <div className="md:col-span-2 lg:col-span-3">
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={isPublic}
                                onChange={(e) => setIsPublic(e.target.checked)}
                                className="sr-only"
                            />
                            <div className={`relative w-5 h-5 rounded border-2 transition-colors duration-200 ${
                                isPublic ? 'bg-emerald-500 border-emerald-500' : 'border-gray-400'
                            }`}>
                                {isPublic && (
                                    <CheckCircleIcon className="h-3 w-3 text-white absolute top-0.5 left-0.5" />
                                )}
                            </div>
                            <div className="ml-3">
                                <span className="text-white font-medium">Make this class public</span>
                                <p className="text-gray-400 text-sm">Allow non-venue users to book this class</p>
                            </div>
                            <GlobeAltIcon className="h-5 w-5 text-emerald-400 ml-2" />
                        </label>
                    </div>
                </div>

                <div className="flex justify-end mt-6">
                    <button
                        onClick={handleAddClass}
                        className="btn-primary flex items-center"
                        disabled={!className || !currentVenueKey}
                    >
                        <PlusIcon className="h-5 w-5 mr-2" />
                        Add to List
                    </button>
                </div>
            </div>

            {/* Preview Table */}
            {classes.length > 0 && (
                <div className="card overflow-hidden mb-6">
                    <div className="bg-gray-700 px-6 py-4 border-b border-gray-600">
                        <h3 className="text-lg font-bold text-white flex items-center">
                            <CalendarIcon className="h-6 w-6 mr-2" />
                            Classes to Create ({classes.length})
                        </h3>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-700">
                            <thead className="table-header">
                                <tr>
                                    <th className="px-6 py-3 text-left">Class Details</th>
                                    <th className="px-6 py-3 text-left">Schedule</th>
                                    <th className="px-6 py-3 text-left">Price</th>
                                    <th className="px-6 py-3 text-left">Settings</th>
                                    <th className="px-6 py-3 text-left">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                                {classes.map((c, idx) => (
                                    <tr key={idx} className="hover:bg-gray-700 transition-colors">
                                        <td className="table-cell">
                                            <div>
                                                <div className="text-sm font-medium text-white">
                                                    {c.name}
                                                </div>
                                                <div className="text-sm text-gray-400">
                                                    {c.description}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="table-cell">
                                            <div>
                                                <div className="text-sm text-white">
                                                    {dayjs(c.startDate).format('DD/MM/YYYY')}
                                                </div>
                                                <div className="text-sm text-gray-400">
                                                    {dayjs(c.startDate).format('HH:mm')}
                                                </div>
                                                <div className="text-xs text-emerald-400 font-medium">
                                                    {cycleTypes.find(type => type.value === c.cycleType)?.label || 'Weekly'}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="table-cell">
                                            <span className="text-white font-medium">
                                                £{c.price.toFixed(2)}
                                            </span>
                                        </td>
                                        <td className="table-cell">
                                            <div className="flex flex-col space-y-1">
                                                {c.isPublic && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-900 text-emerald-200">
                                                        <GlobeAltIcon className="h-3 w-3 mr-1" />
                                                        Public
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="table-cell">
                                            <button
                                                onClick={() => handleRemoveClass(idx)}
                                                className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                                                title="Remove class"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Submit Button */}
            {classes.length > 0 && (
                <div className="flex justify-center">
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className={`text-lg px-8 py-3 flex items-center ${
                            isSubmitting 
                                ? 'bg-gray-600 cursor-not-allowed text-gray-400' 
                                : 'btn-success'
                        }`}
                    >
                        {isSubmitting ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400 mr-2"></div>
                                Creating Classes...
                            </>
                        ) : (
                            <>
                                <CheckCircleIcon className="h-6 w-6 mr-2" />
                                Create All {classes.length} Classes
                            </>
                        )}
                    </button>
                </div>
            )}

            {/* Empty State */}
            {classes.length === 0 && (
                <div className="card p-8 text-center">
                    <CalendarIcon className="h-12 w-12 text-gray-500 mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-gray-400 mb-1">No Classes Added</h3>
                    <p className="text-gray-500">Fill out the form above and click "Add to List" to start creating classes.</p>
                </div>
            )}
        </div>
    );
};

export default BulkClass;